import { Box, type GridProps, Text, VStack } from "@chakra-ui/layout";
import { Button, HStack, Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { Avatar } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { UncollapseOnHover } from "@src/components/ui-kit/UncollapseOnHover";
import { UserTooltip } from "@src/components/ui-kit/UserTooltip";
import { ProjectCell } from "@src/components/widgets/TableCell";
import { percent } from "@src/utils/formatters";
import type { ReactNode } from "react";
import { ProjectProfitModel } from "../profit/models";
import type { ProjectReportBaseModel } from "./models";
import type {
  DetailSummary,
  ProjectReportColumn,
  ProjectReportDetailClick,
} from "./types";

export const DETAIL_TEMPLATE_COLUMN: GridProps["gridTemplateColumns"] =
  // NOTE: Use this when COSTS will be added to modal
  // "1fr repeat(5, 0.85fr)";
  "0.25fr repeat(4, 0.2fr)";

function renderHeaderWithTooltip(
  label: ReactNode,
  tooltip?: ReactNode,
): ReactNode {
  if (!tooltip) {
    return label;
  }

  return (
    <HStack spacing="1">
      <Box>{label}</Box>
      <Tooltip
        label={
          <Box minW="100px" maxW="300px" p="3">
            {tooltip}
          </Box>
        }
      >
        <Box>
          <Icon
            color="#919EAB"
            name="info-circle"
            style={{ transform: "scale(0.8)" }}
            variant="solid"
          />
        </Box>
      </Tooltip>
    </HStack>
  );
}

export function withProjectHeaderTooltip(
  label: ReactNode,
  tooltip?: ReactNode,
): () => ReactNode {
  return () => renderHeaderWithTooltip(label, tooltip);
}

type CreateProjectColumnsParams = {
  onDetailClick: ProjectReportDetailClick;
};

export function createProjectColumns<Row extends ProjectReportBaseModel>({
  onDetailClick,
}: CreateProjectColumnsParams): ProjectReportColumn<
  Row & Record<string, unknown>
>[] {
  return [
    {
      id: "PM",
      meta: { label: "PM" },
      header: "PM",
      cell: ({ row }) => (
        <UserTooltip user={row.original.projectManager}>
          <Avatar
            name={row.original.projectManager.full_name}
            src={row.original.projectManager.image?.urls.thumbnail}
            colorScheme={row.original.projectManager.profile?.hex_color}
          />
        </UserTooltip>
      ),
    },
    {
      id: "project",
      meta: { label: t`Project`, align: "center" },
      header: () => t`Project`,
      cell: ({ row }) => (
        <UncollapseOnHover
          width="24"
          dataComponentName="project-report-detail"
          hiddenComponent={
            <Button
              ml="2"
              onClick={() => {
                onDetailClick(
                  row.original.id,
                  row.original.gross_income_for_date_range,
                );
              }}
              size="xs"
              variant="outline"
            >
              <Trans>Show details</Trans>
            </Button>
          }
        >
          <Box textAlign="left">
            <ProjectCell.Title
              project={{
                id: row.original.id,
                code: row.original.code,
                title: row.original.title,
                categories: [],
              }}
              brand={row.original.brand}
            />
          </Box>
        </UncollapseOnHover>
      ),
    },
  ];
}

export function createRevenueShareCol(): ProjectReportColumn {
  return {
    id: "invoiced-share",
    meta: { label: t`Share Invoiced (%)`, align: "right" },
    header: withProjectHeaderTooltip(
      <Trans>Share Invoiced (%)</Trans>,
      <Trans>
        Percentage of total invoicing for the selected period and applied
        filters.
      </Trans>,
    ),
    cell: ({ row }) => {
      const report = row.original;

      if (report instanceof ProjectProfitModel) {
        return null;
      }

      return (
        <VStack align="right" pr="5">
          <Text>{percent.format(report.filtered_revenue_share)}</Text>
          <Text color="gray.400">
            {percent.format(report.total_revenue_share)}
          </Text>
        </VStack>
      );
    },
  };
}

export const EMPTY_SUMMARY: DetailSummary = {
  billings: 0,
  netMargin: 0,
  netProfit: 0,
};
